import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Check";
import { ComboBox } from "..";
import { userActions } from "../../redux/actions/users/UserActions";
import { organizationActions } from "../../redux/actions/guides/OrganizationActions";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import SimpleBackdrop from "../Backdrop/SimpleBackdrop";
import { Checkbox, Divider, FormControlLabel, Grid } from "@material-ui/core";
import { storageActions } from "../../redux/actions/guides";
import { useModalManager } from "../../hooks/useModalManager";

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2, 3),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  typography: {
    padding: theme.spacing(2),
  },
  statusPopover: {
    position: "absolute",
    right: theme.spacing(7),
    top: theme.spacing(2),
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;

  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      {children}
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const defaultValues = {
  defaultOrganizationId: null,
  defaultStorageId: null,
  showFilter: false,
};

const validationSchema = Yup.object().shape({
  defaultOrganizationId: Yup.object().nullable(),
  defaultStorageId: Yup.object().nullable(),
  showFilter: Yup.boolean(),
});

const SettingModal = ({ modalShow, onClose }) => {
  useModalManager(modalShow, onClose);
  const dispatch = useDispatch();
  const { user, updateDefaultSettings } = useAuth();

  const organizations = useSelector((state) => state.guides.organizations.items);
  const loadingOrganizations = useSelector((state) => state.guides.organizations.loadingItems);

  const { items: storages, fetchedItems: fetchedStorages, loadingItems: loadingStorages } = useSelector((state) => state.guides.storages);

  const currentUser = useSelector((state) => state.users.usersList.item);
  const loading = useSelector((state) => state.users.usersList.loadingItem);
  const fetchedUser = useSelector((state) => state.users.usersList.fetchedItem);
  const currentOrganization = useSelector((state) => state.users.usersList.organization);
  const currentStorage = useSelector((state) => state.users.usersList.storage);

  const { control, handleSubmit, clearErrors, setValue, reset, watch } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValues,
  });

  const watchedOrganizationId = watch("defaultOrganizationId")?.id;

  const filteredStorages = useMemo(() => {
    if (!watchedOrganizationId) return [];
    return storages.filter((s) => s.organizationId === watchedOrganizationId);
  }, [storages, watchedOrganizationId]);

  useEffect(() => {
    if (watchedOrganizationId) {
      setValue("defaultStorageId", null);
    }
  }, [watchedOrganizationId, setValue]);

  const onSubmit = async (data) => {
    const orgId = data.defaultOrganizationId?.id ?? null;
    const storageId = data.defaultStorageId?.id ?? null;
    const showFilter = data.showFilter === true;

    try {
      const response = await dispatch(
        userActions.ChangeDefaultSetting(user.id, user.defaultOrganizationId, {
          defaultOrganizationId: orgId,
          defaultStorageId: storageId,
          showFilter: showFilter,
        })
      );

      updateDefaultSettings(orgId, storageId);

      closeModal();
    } catch (error) {

    }
  };

  const closeModal = () => {
    onClose();
    clearErrors();
    reset(defaultValues);
  };

  useEffect(() => {
    if (modalShow) {
      dispatch(organizationActions.getAll());
      dispatch(userActions.getById(user.id, user.id));
      dispatch(storageActions.getAll({
        isArchive: "No",
        includeAll: true
      }));
    }
  }, [modalShow]);

  useEffect(() => {
    setValue(
      "defaultOrganizationId",
      currentOrganization
        ? {
          id: currentOrganization.id,
          title: currentOrganization.nameOrganization + " (ИНН: " + currentOrganization.inn + ")",
        }
        : null
    );
  }, [currentUser, organizations, currentOrganization]);

  useEffect(() => {
    setValue(
      "defaultStorageId",
      currentStorage
        ? {
          id: currentStorage.id,
          title: currentStorage.nameStorage,
        }
        : null
    );
  }, [currentUser, storages, currentStorage]);

  useEffect(() => {
    setValue("showFilter", currentUser?.showFilter === true);
  }, [currentUser]);

  return (
    <Dialog open={modalShow} onClose={closeModal} aria-labelledby="setting-dialog" fullWidth={true} maxWidth="sm">
      <DialogTitle id="setting-dialog" onClose={closeModal}>
        <Typography color="inherit" variant="h6" display="inline">
          Настройки
        </Typography>
      </DialogTitle>

      <MuiDialogContent dividers style={{ position: "relative" }}>
        {/* Секция: Общие */}
        <div className="mb-6">
          <h6 className="m-0 mb-3 font-medium text-muted">Общие</h6>
          <ComboBox
            control={control}
            name="defaultOrganizationId"
            options={organizations.map((elem) => ({
              id: elem.id,
              title: elem.nameOrganization + " (ИНН: " + elem.inn + ")",
            }))}
            label="Организация по умолчанию"
            loading={loadingOrganizations}
          />
          <ComboBox
            control={control}
            name="defaultStorageId"
            options={filteredStorages.map((elem) => ({
              id: elem.id,
              title: elem.nameStorage,
            }))}
            label="Склад по умолчанию"
            loading={loadingStorages}
            placeholder={!watchedOrganizationId ? "Сначала выберите организацию" : "Выберите склад"}
          />
        </div>

        <Divider className="mb-6" />

        {/* Секция: Интерфейс */}
        <div>
          <h6 className="m-0 mb-3 font-medium text-muted">Интерфейс</h6>
          <Controller
            name="showFilter"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    color="primary"
                  />
                }
                label="Показывать панель фильтров по умолчанию"
              />
            )}
          />
        </div>

        {loading && <SimpleBackdrop show={loading} />}
      </MuiDialogContent>

      <MuiDialogActions>
        <Grid container spacing={1} className="mx-3 my-1 justify-end">
          <Grid item xs={12} sm={6} md={5}>
            <Button fullWidth variant="contained" color="primary" disableElevation disabled={loading} onClick={handleSubmit(onSubmit)} startIcon={<SaveIcon />}>
              Применить настройки
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Button fullWidth variant="outlined" color="inherit" onClick={closeModal}>
              Закрыть
            </Button>
          </Grid>
        </Grid>
      </MuiDialogActions>
    </Dialog>
  );
};

export default SettingModal;